/**
 * SA-locale number formatters for KPI cards / charts (fleetGUARDIAN parity).
 * Mirrors the helper strings in the source app's `dxChartTheme.ts` so digits,
 * grouping, and abbreviations read identically. All numeric displays should be
 * rendered with `tabular-nums` so figures don't jitter as they animate.
 */

const ZAR = new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 0 });
const ZAR2 = new Intl.NumberFormat('en-ZA', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const COMPACT = new Intl.NumberFormat('en-ZA', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

/** `R 1,234,567` — full ZAR. */
export function fmtR(v: number): string {
  return `R ${ZAR.format(Math.round(v))}`;
}

/** `R 35k` / `R 1.2M` — compact ZAR for axis ticks. */
export function fmtRShort(v: number): string {
  const a = Math.abs(v);
  if (a >= 1_000_000) return `R ${(v / 1_000_000).toFixed(1)}M`;
  if (a >= 1_000) return `R ${(v / 1_000).toFixed(0)}k`;
  return `R ${v.toFixed(0)}`;
}

/** `R 20.49` — ZAR with cents (weighted-avg prices). */
export function fmtR2(v: number): string {
  return `R ${ZAR2.format(v)}`;
}

/** `1,234,567 L` — full litres. */
export function fmtL(v: number): string {
  return `${ZAR.format(Math.round(v))} L`;
}

/** `28k` / `1.2M` — compact litres for axis ticks / chips. */
export function fmtLShort(v: number): string {
  const a = Math.abs(v);
  if (a >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (a >= 1_000) return `${(v / 1_000).toFixed(0)}k`;
  return `${v.toFixed(0)}`;
}

/** `1,234,567` — plain grouped integer. */
export function fmtNum(v: number): string {
  return ZAR.format(Math.round(v));
}

/** `1.2M` — compact plain number. */
export function fmtCompact(v: number): string {
  return COMPACT.format(v);
}

/** `12.3%` — percentage with configurable precision. */
export function fmtPct(v: number, digits = 1): string {
  return `${v.toFixed(digits)}%`;
}

/** `12 units` / `1 unit` — auto-pluralised count. */
export function fmtCount(v: number, unit = 'unit'): string {
  return `${ZAR.format(v)} ${unit}${v === 1 ? '' : 's'}`;
}
